import { supabase } from './supabase';

export type EmailTemplateType =
  | 'account_registration'
  | 'email_verification'
  | 'password_reset'
  | 'welcome_email'
  | 'service_request_confirmation'
  | 'quotation_notification'
  | 'quotation_accepted'
  | 'invoice_notification'
  | 'payment_instructions'
  | 'receipt_submitted'
  | 'payment_approved'
  | 'payment_rejected'
  | 'more_info_requested'
  | 'project_update'
  | 'support_ticket_created'
  | 'support_ticket_response'
  | 'course_enrollment'
  | 'course_completion'
  | 'certificate_issued'
  | 'renewal_reminder';

export interface EmailPayload {
  recipient: string;
  recipientName?: string;
  subject: string;
  templateType: EmailTemplateType;
  referenceNumber?: string;
  actionUrl?: string;
  details?: Record<string, any>;
}

export interface EmailLogResult {
  id?: string;
  recipient: string;
  subject: string;
  templateType: string;
  status: 'Sent' | 'Failed';
  referenceNumber?: string;
  error?: string;
}

const BRAND_HEADER = `
  <div style="background-color: #0B1A3A; padding: 24px; text-align: center; border-radius: 8px 8px 0 0;">
    <h1 style="color: #FFFFFF; margin: 0; font-size: 22px; font-family: Helvetica, Arial, sans-serif; letter-spacing: 1px;">BIENTOMART</h1>
    <p style="color: #93C5FD; margin: 4px 0 0; font-size: 12px; font-family: Helvetica, Arial, sans-serif;">Digital Business Platform & Online Academy</p>
  </div>
`;

const BRAND_FOOTER = `
  <div style="background-color: #F8FAFC; padding: 20px; text-align: center; border-radius: 0 0 8px 8px; border-top: 1px solid #E2E8F0; font-family: Helvetica, Arial, sans-serif;">
    <p style="color: #64748B; font-size: 11px; margin: 0 0 6px;">Plot 12 Kampala Road, Asante Media Agency, Kampala, Uganda</p>
    <p style="color: #64748B; font-size: 11px; margin: 0 0 10px;">Support: +256 773 515 447 | bientomart@gmail.com | www.bientomart.com</p>
    <p style="color: #94A3B8; font-size: 10px; margin: 0;">&copy; 2026 BientoMart Digital Solutions. All rights reserved.</p>
  </div>
`;

/**
 * Builds standard responsive HTML email body with BientoMart branding
 */
export function buildEmailHtml(payload: EmailPayload): string {
  const { recipientName = 'Valued Partner', subject, referenceNumber, actionUrl, details = {} } = payload;

  let bodyContent = '';

  switch (payload.templateType) {
    case 'account_registration':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Account Created Successfully</h2>
        <p>Dear ${recipientName},</p>
        <p>Your BientoMart account has been provisioned. You can now access your client services and academy courses.</p>
        ${actionUrl ? `<p><a href="${actionUrl}" style="background-color: #0B1A3A; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; font-size: 13px;">Access Your Account</a></p>` : ''}
      `;
      break;

    case 'email_verification':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Verify Your Email Address</h2>
        <p>Dear ${recipientName},</p>
        <p>Please confirm your email address to activate your full account permissions and secure communication channels.</p>
        ${actionUrl ? `<p><a href="${actionUrl}" style="background-color: #2D73ED; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; font-size: 13px;">Verify Email</a></p>` : ''}
      `;
      break;

    case 'password_reset':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Password Reset Instructions</h2>
        <p>Dear ${recipientName},</p>
        <p>A request was received to reset your password. If you initiated this, click below to set a new password securely.</p>
        ${actionUrl ? `<p><a href="${actionUrl}" style="background-color: #0B1A3A; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; font-size: 13px;">Reset Password</a></p>` : ''}
      `;
      break;

    case 'welcome_email':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Welcome to BientoMart!</h2>
        <p>Dear ${recipientName},</p>
        <p>We are delighted to welcome you to BientoMart. Our team is committed to delivering world-class digital systems, web development, and accredited training.</p>
      `;
      break;

    case 'service_request_confirmation':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Service Inquiry Received</h2>
        <p>Dear ${recipientName},</p>
        <p>Thank you for commissioning BientoMart. Your request has been assigned to our operations team.</p>
        <p style="background: #F1F5F9; padding: 12px; border-left: 4px solid #2D73ED; font-size: 13px;">
          <strong>Reference:</strong> ${referenceNumber || 'SR-PENDING'}<br/>
          <strong>Service:</strong> ${details.serviceName || 'Custom Digital Service'}<br/>
          <strong>Timeline:</strong> ${details.timeline || 'Standard'}
        </p>
      `;
      break;

    case 'quotation_notification':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Formal Quotation Ready</h2>
        <p>Dear ${recipientName},</p>
        <p>Our team has prepared your formal quotation for review.</p>
        <p style="background: #F1F5F9; padding: 12px; border-left: 4px solid #10B981; font-size: 13px;">
          <strong>Quote Number:</strong> ${referenceNumber}<br/>
          <strong>Total Amount:</strong> $${details.totalAmount || '0.00'} USD<br/>
          <strong>Valid Until:</strong> ${details.validUntil || '30 days from issuance'}
        </p>
        ${actionUrl ? `<p><a href="${actionUrl}" style="background-color: #10B981; color: white; padding: 10px 20px; text-decoration: none; border-radius: 6px; display: inline-block; font-weight: bold; font-size: 13px;">Review & Accept Quotation</a></p>` : ''}
      `;
      break;

    case 'quotation_accepted':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Quotation Accepted</h2>
        <p>Dear ${recipientName},</p>
        <p>We confirm that Quotation <strong>#${referenceNumber}</strong> has been officially accepted. An invoice and manual payment instructions have been generated.</p>
      `;
      break;

    case 'invoice_notification':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Invoice Issued</h2>
        <p>Dear ${recipientName},</p>
        <p>An official invoice has been issued for your commissioned services.</p>
        <p style="background: #F1F5F9; padding: 12px; border-left: 4px solid #0B1A3A; font-size: 13px;">
          <strong>Invoice Number:</strong> ${referenceNumber}<br/>
          <strong>Amount Due:</strong> $${details.amountDue || '0.00'} USD<br/>
          <strong>Due Date:</strong> ${details.dueDate || 'Upon receipt'}
        </p>
        <p>Please review our official BientoMart Mobile Money and Bank wire payment instructions in your client portal.</p>
      `;
      break;

    case 'payment_instructions':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Official Payment Instructions</h2>
        <p>Dear ${recipientName},</p>
        <p>To settle Invoice <strong>#${referenceNumber}</strong>, please complete payment externally through any of our authorized payment channels:</p>
        <div style="background: #EFF6FF; padding: 14px; border-radius: 6px; font-size: 12px; line-height: 1.6; margin-bottom: 15px;">
          <strong style="color: #1E40AF;">1. MTN Mobile Money (Uganda):</strong> +256 773 515 447 (Bienvenu Masengesho)<br/>
          <strong style="color: #1E40AF;">2. Airtel Money (Uganda):</strong> +256 754 515 447 (Bienvenu Masengesho)<br/>
          <strong style="color: #1E40AF;">3. Stanbic Bank Uganda:</strong> A/C 9030018472910 (BientoMart Digital Solutions)
        </div>
        <p><strong>Next Step:</strong> After paying, upload your receipt image or transaction confirmation in your portal for review.</p>
      `;
      break;

    case 'receipt_submitted':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Payment Receipt Under Review</h2>
        <p>Dear ${recipientName},</p>
        <p>We have received your payment receipt for reference <strong>#${referenceNumber}</strong>. Status has been updated to <strong>PAYMENT UNDER REVIEW</strong>.</p>
        <p>Our Finance Officers will verify the funds with our banking partners and approve your project / course enrollment within 2 business hours.</p>
      `;
      break;

    case 'payment_approved':
      bodyContent = `
        <h2 style="color: #10B981; margin-top: 0;">Payment Verified & Approved</h2>
        <p>Dear ${recipientName},</p>
        <p>Your payment for <strong>#${referenceNumber}</strong> has been successfully verified and approved by the BientoMart Finance Department.</p>
        <p>Your project / course enrollment is now officially <strong>ACTIVE</strong>.</p>
      `;
      break;

    case 'payment_rejected':
      bodyContent = `
        <h2 style="color: #EF4444; margin-top: 0;">Payment Verification Notice</h2>
        <p>Dear ${recipientName},</p>
        <p>We were unable to verify your payment for reference <strong>#${referenceNumber}</strong>.</p>
        <p style="background: #FEF2F2; padding: 12px; border-left: 4px solid #EF4444; color: #991B1B; font-size: 13px;">
          <strong>Reason:</strong> ${details.reason || 'The transaction reference could not be matched with bank statements.'}
        </p>
        <p>Please re-check your payment receipt or contact finance support.</p>
      `;
      break;

    case 'more_info_requested':
      bodyContent = `
        <h2 style="color: #D97706; margin-top: 0;">Additional Information Required for Payment</h2>
        <p>Dear ${recipientName},</p>
        <p>Our finance officer requested additional verification details for reference <strong>#${referenceNumber}</strong>:</p>
        <p style="background: #FFFBEB; padding: 12px; border-left: 4px solid #D97706; color: #92400E; font-size: 13px;">
          <strong>Note from Finance:</strong> ${details.reason || 'Please provide a clear full screenshot displaying the sender phone number and SMS timestamp.'}
        </p>
        <p>Please log in to your portal and re-upload the requested information.</p>
      `;
      break;

    case 'project_update':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Project Progress Update</h2>
        <p>Dear ${recipientName},</p>
        <p>There is an update on your project <strong>#${referenceNumber}</strong>: ${details.milestoneName || 'Milestone updated'}.</p>
        <p>Current Progress: <strong>${details.progress || '50'}%</strong></p>
      `;
      break;

    case 'support_ticket_created':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Support Request Opened</h2>
        <p>Dear ${recipientName},</p>
        <p>Your support ticket <strong>#${referenceNumber}</strong> has been logged into our support queue. A specialist will assist you promptly.</p>
      `;
      break;

    case 'support_ticket_response':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">New Response on Support Ticket</h2>
        <p>Dear ${recipientName},</p>
        <p>Our technical team responded to ticket <strong>#${referenceNumber}</strong>.</p>
        <p style="background: #F8FAFC; padding: 12px; border-left: 4px solid #2D73ED; font-size: 13px; font-style: italic;">
          "${details.messagePreview || 'We have analyzed the situation and applied the update...'}"
        </p>
      `;
      break;

    case 'course_enrollment':
      bodyContent = `
        <h2 style="color: #0B1A3A; margin-top: 0;">Enrolled in Course</h2>
        <p>Dear ${recipientName},</p>
        <p>Welcome to <strong>${details.courseTitle || 'BientoMart Academy'}</strong>! Your learning seat has been confirmed.</p>
      `;
      break;

    case 'course_completion':
      bodyContent = `
        <h2 style="color: #10B981; margin-top: 0;">Congratulations on Course Completion!</h2>
        <p>Dear ${recipientName},</p>
        <p>You have successfully completed 100% of the lessons and assessments for <strong>${details.courseTitle || 'your course'}</strong>.</p>
      `;
      break;

    case 'certificate_issued':
      bodyContent = `
        <h2 style="color: #10B981; margin-top: 0;">Verifiable Certificate Issued</h2>
        <p>Dear ${recipientName},</p>
        <p>Your official verifiable certificate for <strong>${details.courseTitle || 'Course'}</strong> is ready.</p>
        <p style="background: #ECFDF5; padding: 12px; border-left: 4px solid #10B981; font-size: 13px;">
          <strong>Certificate ID:</strong> ${referenceNumber || 'BM-CERT'}<br/>
          <strong>Grade:</strong> Distinction
        </p>
      `;
      break;

    case 'renewal_reminder':
      bodyContent = `
        <h2 style="color: #D97706; margin-top: 0;">Service Renewal Reminder</h2>
        <p>Dear ${recipientName},</p>
        <p>Your subscription for <strong>${details.serviceName || 'Domain & Hosting'}</strong> is scheduled for renewal on ${details.renewalDate || 'in 7 days'}.</p>
      `;
      break;

    default:
      bodyContent = `<p>Dear ${recipientName},</p><p>${subject}</p>`;
  }

  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${subject}</title>
      </head>
      <body style="margin: 0; padding: 20px; background-color: #F1F5F9; font-family: Helvetica, Arial, sans-serif;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #FFFFFF; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden;">
          ${BRAND_HEADER}
          <div style="padding: 30px; font-size: 14px; line-height: 1.6; color: #1E293B;">
            ${bodyContent}
          </div>
          ${BRAND_FOOTER}
        </div>
      </body>
    </html>
  `;
}

/**
 * Logs an email event to Supabase `email_logs` table (safely capturing all transactional emails)
 */
export async function sendEmail(payload: EmailPayload): Promise<EmailLogResult> {
  const html = buildEmailHtml(payload);
  const textBody = `${payload.subject}\n\nRecipient: ${payload.recipient}\nReference: ${payload.referenceNumber || 'N/A'}`;

  try {
    const { data, error } = await supabase
      .from('email_logs')
      .insert({
        recipient: payload.recipient,
        subject: payload.subject,
        template_type: payload.templateType,
        reference_number: payload.referenceNumber || '',
        body_html: html,
        body_text: textBody,
        status: 'Sent',
        sent_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.warn('[EmailService] Logging fallback error:', error.message);
      return {
        recipient: payload.recipient,
        subject: payload.subject,
        templateType: payload.templateType,
        status: 'Failed',
        error: error.message,
      };
    }

    return {
      id: data.id,
      recipient: payload.recipient,
      subject: payload.subject,
      templateType: payload.templateType,
      referenceNumber: payload.referenceNumber,
      status: 'Sent',
    };
  } catch (err: any) {
    console.error('[EmailService] Send error:', err);
    return {
      recipient: payload.recipient,
      subject: payload.subject,
      templateType: payload.templateType,
      status: 'Failed',
      error: err.message,
    };
  }
}
